<script>
import { ref } from "vue";
import { useStore } from "vuex";
import { useMaps } from "../composables/useMaps.js";
import useFormFields from "../composables/form-fields.js";
import StaticMap from "../components/StaticMap.vue";
import AutocompleteMaps from "../components/AutocompleteMaps.vue";
export default {
  props: {
    data: {
      required: true,
      type: Object
    },
    type: {
      required: true,
      type: String
    },
    id: {
      required: true,
      type: String
    },
    edit: {
      required: true,
      type: Boolean
    },
    mobile: {
      required: true,
      type: Boolean
    }
  },
  components: {
    StaticMap,
    AutocompleteMaps
  },
  setup(props) {
    const store = useStore();
    const formFields = ref(null);
    const workInProgress = ref(props.type === "client");
    if (props.type === "project") {
      const { PROJECT_FIELDS } = useFormFields();
      formFields.value = PROJECT_FIELDS;
    } else if (props.type === "client") {
      const { CLIENT_FIELDS } = useFormFields();
      formFields.value = CLIENT_FIELDS;
    }

    const formData = ref(Object.assign({}, props.data));
    const objectId = ref(props.id);
    const inputEl = ref(null);
    const reloadMap = ref(false);

    const { setLocation: use_setLocation } = useMaps();
    async function setLocation(place) {
      reloadMap.value = false;
      const location = use_setLocation(place);
      if (props.type === "project") {
        try {
          await store.dispatch("updateProject", {
            projectId: objectId.value,
            updateData: { location: location }
          });

          formData.value.location = location;
          reloadMap.value = true;
        } catch (error) {
          console.log(error);
          return;
        }
      }
    }

    async function onBlur(ev, fieldName) {
      if (inputEl.value === ev.target.value) return;
      if (props.type === "client") {
        try {
          await store.dispatch("updateClient", {
            clientId: objectId.value,
            updateData: formData.value
          });
        } catch (error) {
          errorHandler(ev, fieldName);
          return;
        }
      } else if (props.type === "project") {
        try {
          await store.dispatch("updateProject", {
            projectId: objectId.value,
            updateData: formData.value
          });
        } catch (error) {
          errorHandler(ev, fieldName);
          return;
        }
      }
      successHandler(ev);
    }

    function onFocus(ev) {
      inputEl.value = ev.target.value;
    }

    function errorHandler(ev, fieldName) {
      ev.target.classList.add("error");
      setTimeout(() => {
        ev.target.classList.remove("error");
        formData.value[fieldName] = props.data[fieldName];
      }, 2000);
    }

    function successHandler(ev) {
      ev.target.classList.add("success");
      setTimeout(() => {
        ev.target.classList.remove("success");
      }, 2000);
    }

    function showScrollbar(id) {
      const infoCard = document.getElementById(id);
      infoCard.classList.remove("no-scrollbar");
    }

    function hideScrollbar(id) {
      const infoCard = document.getElementById(id);
      infoCard.classList.add("no-scrollbar");
    }

    return {
      workInProgress,
      formFields,
      formData,
      setLocation,
      onBlur,
      onFocus,
      showScrollbar,
      hideScrollbar
    };
  }
};
</script>

<template>
  <div v-if="!workInProgress" class="cards-container">
    <div
      v-for="(fields, i) in formFields.slice(0, -1)"
      :key="i"
      :id="i"
      class="card no-scrollbar custom-scrollbar"
      @mouseenter="showScrollbar(i)"
      @mouseleave="hideScrollbar(i)"
    >
      <div v-for="(field, ind) in fields" :key="ind" class="sub-card">
        <div class="title">
          {{ field.display }}
        </div>
        <transition mode="out-in">
          <div v-if="!edit" class="data">
            {{ formData[field.name] }} {{ field.detail ? field.detail : null }}
          </div>
          <div v-else>
            <InputText
              v-if="field.name !== 'notes'"
              @blur="onBlur($event, field.name)"
              @focus="onFocus"
              type="text"
              v-model.trim="formData[field.name]"
            />
            <Textarea
              v-else
              @blur="onBlur($event, 'notes')"
              @focus="onFocus"
              v-model.trim="formData.notes"
              rows="5"
            />
          </div>
        </transition>
      </div>
    </div>
    <div
      v-if="type === 'project'"
      class="info-card no-scrollbar card"
      style="padding: 0;"
    >
      <div class="map-card">
        <static-map
          :reloadMap="reloadMap"
          :mapCenter="formData.location.coords"
          :mapUrl="formData.location.url"
        ></static-map>
        <transition mode="out-in">
          <autocomplete-maps
            v-if="edit"
            id="address"
            @place-changed="setLocation"
          ></autocomplete-maps>
          <div v-else></div>
        </transition>
      </div>
    </div>
  </div>
  <div v-else class="work-progress">
    Work in Progress
  </div>
</template>

<style lang="scss" scoped>
.cards-container {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
}

.card {
  height: 30vh;
  box-shadow: 3px 3px 7px rgba(1, 1, 1, 0.5);
  border-radius: 6px;
  text-align: center;
  background: var(--surface-200);
  overflow-y: overlay;
}
.map-card input {
  position: absolute;
  top: 0;
  left: 0;
}

.info-card {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  text-align: center;
  position: relative;
  height: 30vh;
  background: var(--surface-200);
  border-radius: 6px;
  padding: 1rem 0;
  overflow-y: overlay;
}

.map-card {
  width: 100%;
  height: 100%;
  position: relative;
}

.sub-card {
  width: 100%;
  padding: 0 1rem;
  &:first-child {
    min-height: 50%;
  }
  &:last-child {
    .title {
      position: -webkit-sticky;
      position: sticky;
      top: -1rem;
    }
  }
}

.title {
  font-size: 1rem;
  font-weight: bold;
  background: var(--surface-200);
}

.data {
  font-size: 1.5rem;
}

.p-component:disabled {
  opacity: 1;
  border-color: transparent;
}

.error {
  box-shadow: 0 0 0 0.2rem red;
}

.success {
  box-shadow: 0 0 0 0.2rem green;
}

.p-inputtext {
  width: 100%;
  text-align: center;
}

.work-progress {
  font-weight: 700;
  font-size: 2rem;
  text-align: center;
}

/* Animations */
.v-enter-from {
  opacity: 0;
  transform: translateY(-30px);
}

.v-enter-to,
.v-leave-from {
  opacity: 1;
  transform: translateY(0);
}

.v-leave-to {
  opacity: 0;
  transform: translateY(30px);
}

.v-leave-active {
  transition: all 0.1s ease-in;
}
.v-enter-active {
  transition: all 0.1s ease-out;
}
</style>
